/**
 * @module components/CountLimit
 * @description 词云展示数量限制 — 仅词云图使用，上传字段 count，默认 30
 *
 * 状态管理模式：
 *   读: useSettingState('count')；未落库时展示 DEFAULT_WORDCLOUD_COUNT（与 TopN 共用 key，Schema 默认仍为 TopN）
 *   写: setValue(count)
 */

import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { Input } from 'tea-component';
import TipContext from '@home/components/TipContext';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import {
  useChartSettingsSelector,
  useSettingState,
} from '../../context';
import { DEFAULT_WORDCLOUD_COUNT } from '../../utils/constants';
import './index.scss';

const MIN_COUNT = 1;
const MAX_COUNT = 500;

/** 词云展示数量限制 */
const CountLimit: React.FC = () => {
  const { formatMessage } = useIntl();
  const { value: schemaCount, setValue: setCount } = useSettingState('count');
  const hasStoredCount = useChartSettingsSelector(
    s => 'count' in s.draft || 'count' in s.base,
  );
  const value = hasStoredCount ? schemaCount : DEFAULT_WORDCLOUD_COUNT;

  const handleChange = useCallback(
    (inputValue: string) => {
      const num = Number(inputValue);
      if (num < MIN_COUNT) {
        setCount(MIN_COUNT);
        return;
      }
      if (Number.isInteger(num)) {
        setCount(num > MAX_COUNT ? MAX_COUNT : num);
      }
    },
    [setCount],
  );

  return (
    <ConfigCollapseCard
      title={formatMessage({ id: 'dbl_chart_count_limit' })}
    >
      <div className={classNames('dbl-chart-count-limit')}>
        <div className="dbl-chart-count-limit__label">
          <div>{formatMessage({ id: 'dbl_chart_top' })}</div>
          <TipContext
            tipText={formatMessage({ id: 'dbl_chart_count_limit_info' })}
          />
        </div>
        <Input
          type="number"
          size="s"
          value={String(value)}
          onChange={handleChange}
          onPressEnter={(_, eve) => {
            eve.preventDefault();
          }}
          min={MIN_COUNT}
          max={MAX_COUNT}
          step={1}
          style={{ width: '210px' }}
        />
      </div>
      <div className="dbl-chart-count-limit-tip">
        {formatMessage({ id: 'dbl_chart_count_limit_tip' })}
      </div>
    </ConfigCollapseCard>
  );
};

export default React.memo(CountLimit);
